(() => {
  'use strict';

  document.documentElement.classList.remove('no-js');
  document.documentElement.classList.add('js');

  const q = (selector, root = document) => root?.querySelector?.(selector) || null;
  const qa = (selector, root = document) => root?.querySelectorAll ? Array.from(root.querySelectorAll(selector)) : [];
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const lerp = (from, to, amount) => from + (to - from) * amount;
  const smoothstep = (value) => value * value * (3 - 2 * value);
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;

  const formatTime = (seconds) => {
    if (!Number.isFinite(seconds)) return '--:--';
    const total = Math.max(0, Math.floor(seconds));
    return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
  };

  const safeStorage = {
    get(key) {
      try { return sessionStorage.getItem(key); } catch (_) { return null; }
    },
    set(key, value) {
      try { sessionStorage.setItem(key, value); } catch (_) { /* optional */ }
    }
  };

  /* Header */
  const initHeader = () => {
    const header = q('[data-header]');
    if (!header || header.dataset.initialized) return;
    header.dataset.initialized = 'true';

    const navTrigger = q('[data-nav-trigger]', header);
    const nav = q('[data-nav]', header);
    const updateHeader = () => header.classList.toggle('scrolled', scrollY > 24);
    updateHeader();
    addEventListener('scroll', updateHeader, { passive: true });

    navTrigger?.addEventListener('click', () => {
      const open = nav?.classList.toggle('open') || false;
      navTrigger.setAttribute('aria-expanded', String(open));
    });

    qa('a', nav).forEach((link) => link.addEventListener('click', () => {
      nav?.classList.remove('open');
      navTrigger?.setAttribute('aria-expanded', 'false');
    }));
  };

  /* Opening transmission: the storefront is fully locked until the visitor enters. */
  const createIntroStarfield = (canvas) => {
    if (!canvas || canvas.dataset.initialized) return null;
    canvas.dataset.initialized = 'true';
    const context = canvas.getContext('2d', { alpha: true });
    if (!context) return null;

    let width = 1;
    let height = 1;
    let dpr = 1;
    let stars = [];
    let lastFrame = 0;
    let raf = 0;
    let active = true;

    const seededRandom = (seed) => {
      let value = seed >>> 0;
      return () => {
        value += 0x6D2B79F5;
        let t = value;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
      };
    };

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      width = Math.max(1, rect.width);
      height = Math.max(1, rect.height);
      dpr = Math.min(devicePixelRatio || 1, innerWidth < 720 ? 1.35 : 1.7);
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      context.setTransform(dpr, 0, 0, dpr, 0, 0);

      const random = seededRandom(370011 + Math.round(width) * 13 + Math.round(height) * 7);
      const count = Math.round(clamp(width * height / 1280, 420, 1180));
      stars = Array.from({ length: count }, (_, index) => {
        const inBand = index < count * .23;
        const x = random();
        const bandCentre = .06 + x * .78 + Math.sin(x * 8.2) * .035;
        const y = inBand ? clamp(bandCentre + (random() + random() + random() - 1.5) * .12, -.05, 1.05) : random();
        const roll = random();
        const tier = roll > .988 ? 2 : roll > .9 ? 1 : 0;
        const temperature = random();
        const color = temperature > .965 ? '255,224,168' : temperature < .09 ? '176,218,255' : '236,243,249';
        return {
          x,
          y,
          depth: .2 + random() * .8,
          size: tier === 2 ? .95 + random() * .55 : tier === 1 ? .52 + random() * .42 : .22 + random() * .36,
          alpha: tier === 2 ? .72 + random() * .22 : tier === 1 ? .36 + random() * .42 : .15 + random() * .4,
          twinkle: .00012 + random() * .00034,
          twinkleDepth: tier === 2 ? .12 : .035 + random() * .105,
          phase: random() * Math.PI * 2,
          drift: (random() - .5) * (.008 + random() * .012),
          tier,
          color
        };
      });
    };

    const drawStar = (star, x, y, alpha) => {
      if (star.tier === 2) {
        const glow = context.createRadialGradient(x, y, 0, x, y, 5.5);
        glow.addColorStop(0, `rgba(${star.color},${alpha * .18})`);
        glow.addColorStop(1, `rgba(${star.color},0)`);
        context.fillStyle = glow;
        context.fillRect(x - 6, y - 6, 12, 12);
        context.fillStyle = `rgba(${star.color},${alpha * .34})`;
        context.fillRect(x - 4.5, y - .22, 9, .44);
        context.fillRect(x - .22, y - 4.5, .44, 9);
      }
      context.fillStyle = `rgba(${star.color},${alpha})`;
      const size = star.size;
      context.fillRect(x - size * .5, y - size * .5, size, size);
    };

    const draw = (time) => {
      if (!active) return;
      raf = requestAnimationFrame(draw);
      if (time - lastFrame < (reducedMotion ? 90 : 34)) return;
      lastFrame = time;
      context.clearRect(0, 0, width, height);

      stars.forEach((star) => {
        const driftX = reducedMotion ? 0 : Math.sin(time * .000012 + star.phase) * star.drift * star.depth;
        const driftY = reducedMotion ? 0 : Math.cos(time * .000009 + star.phase) * star.drift * .55 * star.depth;
        const pulse = 1 - star.twinkleDepth * .5 + Math.sin(time * star.twinkle + star.phase) * star.twinkleDepth * .5;
        const alpha = clamp(star.alpha * pulse, .03, .96);
        drawStar(star, star.x * width + driftX, star.y * height + driftY, alpha);
      });
    };

    addEventListener('resize', resize, { passive: true });
    resize();
    raf = requestAnimationFrame(draw);

    return {
      destroy() {
        active = false;
        cancelAnimationFrame(raf);
        removeEventListener('resize', resize);
      }
    };
  };

  const initIntro = (hero, onDismiss) => {
    const intro = q('[data-intro]', hero);
    if (!intro) {
      onDismiss?.();
      return;
    }

    const enter = q('[data-enter]', intro);
    const line = q('[data-intro-line]', intro);
    const alreadySeen = safeStorage.get('tnmmIntroV63') === 'seen';
    let starfield = null;
    let finished = false;

    const unlockPage = () => {
      document.documentElement.classList.remove('intro-active');
      document.body.classList.remove('intro-active', 'intro-gate-pending');
      document.body.style.removeProperty('--intro-lock-offset');
      hero.classList.remove('intro-locked');
      window.scrollTo(0, 0);
    };

    const lockPage = () => {
      window.scrollTo(0, 0);
      document.documentElement.classList.add('intro-active');
      document.body.classList.add('intro-active');
      document.body.style.setProperty('--intro-lock-offset', '0px');
      hero.classList.add('intro-locked');
    };

    const finish = () => {
      if (finished) return;
      finished = true;
      starfield?.destroy();
      intro.remove();
      unlockPage();
      onDismiss?.();
    };

    const dismiss = () => {
      if (intro.classList.contains('dismissed')) return;
      intro.classList.add('dismissed');
      safeStorage.set('tnmmIntroV63', 'seen');
      window.setTimeout(finish, 920);
    };

    if (alreadySeen) {
      intro.remove();
      document.documentElement.classList.remove('intro-active');
      document.body.classList.remove('intro-active', 'intro-gate-pending');
      hero.classList.remove('intro-locked');
      onDismiss?.();
      return;
    }

    lockPage();
    starfield = createIntroStarfield(q('[data-intro-starfield]', intro));
    const messages = [
      'CALIBRATING THE LAST KNOWN SKY...',
      'REFERENCE POSITION DOES NOT MATCH.',
      'DISCREPANCY: THIRTY-SEVEN CENTIMETRES.'
    ];
    messages.forEach((message, index) => {
      window.setTimeout(() => {
        if (line && document.contains(line)) line.textContent = message;
      }, 900 + index * 1200);
    });

    enter?.addEventListener('click', dismiss);
    intro.addEventListener('wheel', (event) => event.preventDefault(), { passive: false });
    intro.addEventListener('touchmove', (event) => event.preventDefault(), { passive: false });
    addEventListener('keydown', (event) => {
      if (event.key === 'Enter' && document.contains(intro)) dismiss();
    }, { once: true });
  };

  /* Natural starfield: mostly sub-pixel points, restrained twinkle and almost imperceptible drift. */
  const createStarfield = (canvas, hero) => {
    if (!canvas || canvas.dataset.initialized) return null;
    canvas.dataset.initialized = 'true';
    const context = canvas.getContext('2d', { alpha: true });
    if (!context) return null;

    let width = 1;
    let height = 1;
    let dpr = 1;
    let stars = [];
    let meteors = [];
    let pointerX = 0;
    let pointerY = 0;
    let targetPointerX = 0;
    let targetPointerY = 0;
    let shiftEnergy = 0;
    let lastFrame = 0;
    let nextMeteorAt = performance.now() + 18000 + Math.random() * 16000;
    let visible = true;

    const seededRandom = (seed) => {
      let value = seed >>> 0;
      return () => {
        value += 0x6D2B79F5;
        let t = value;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
      };
    };

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      width = Math.max(1, rect.width);
      height = Math.max(1, rect.height);
      dpr = Math.min(devicePixelRatio || 1, innerWidth < 720 ? 1.35 : 1.65);
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      context.setTransform(dpr, 0, 0, dpr, 0, 0);

      const random = seededRandom(370037 + Math.round(width) * 11 + Math.round(height) * 17);
      const count = reducedMotion ? 260 : Math.round(clamp(width * height / 1320, 420, 1120));
      stars = Array.from({ length: count }, (_, index) => {
        const x = random();
        const inBand = index < count * .24;
        const bandCentre = .08 + x * .76 + Math.sin(x * 7.4) * .028;
        const y = inBand
          ? clamp(bandCentre + (random() + random() + random() - 1.5) * .13, -.045, 1.045)
          : random();
        const roll = random();
        const tier = roll > .991 ? 2 : roll > .91 ? 1 : 0;
        const temperature = random();
        return {
          x,
          y,
          depth: .15 + random() * .85,
          size: tier === 2 ? .95 + random() * .55 : tier === 1 ? .5 + random() * .4 : .22 + random() * .34,
          alpha: tier === 2 ? .68 + random() * .24 : tier === 1 ? .32 + random() * .44 : .12 + random() * .4,
          phase: random() * Math.PI * 2,
          twinkle: .0001 + random() * .00032,
          twinkleDepth: tier === 2 ? .13 : .03 + random() * .1,
          driftX: (random() - .5) * (.000000055 + random() * .000000085),
          driftY: (random() - .5) * (.000000025 + random() * .00000005),
          color: temperature > .972 ? '255,224,170' : temperature < .08 ? '177,220,255' : '235,243,250',
          tier
        };
      });
    };

    const spawnMeteor = () => {
      const fromRight = Math.random() > .35;
      const x = fromRight ? width * (.78 + Math.random() * .22) : width * (.18 + Math.random() * .32);
      const y = height * (.08 + Math.random() * .3);
      meteors.push({
        x,
        y,
        vx: fromRight ? -(2.4 + Math.random() * 1.5) : 2.1 + Math.random() * 1.2,
        vy: 1.15 + Math.random() * 1.1,
        life: 0,
        maxLife: 72 + Math.random() * 42,
        length: 58 + Math.random() * 70
      });
    };

    const drawStar = (star, x, y, alpha) => {
      if (star.tier === 2) {
        const glow = context.createRadialGradient(x, y, 0, x, y, 5.5);
        glow.addColorStop(0, `rgba(${star.color},${alpha * .15})`);
        glow.addColorStop(1, `rgba(${star.color},0)`);
        context.fillStyle = glow;
        context.fillRect(x - 6, y - 6, 12, 12);
        context.fillStyle = `rgba(${star.color},${alpha * .28})`;
        context.fillRect(x - 4.5, y - .2, 9, .4);
        context.fillRect(x - .2, y - 4.5, .4, 9);
      }
      context.fillStyle = `rgba(${star.color},${alpha})`;
      context.fillRect(x - star.size * .5, y - star.size * .5, star.size, star.size);
    };

    const draw = (time) => {
      requestAnimationFrame(draw);
      if (!visible) return;
      if (time - lastFrame < (reducedMotion ? 100 : 34)) return;
      const delta = Math.min(70, Math.max(1, time - lastFrame || 16));
      lastFrame = time;

      pointerX = lerp(pointerX, targetPointerX, .025);
      pointerY = lerp(pointerY, targetPointerY, .025);
      context.clearRect(0, 0, width, height);

      const shiftCenterX = width * .67;
      const shiftCenterY = height * .43;

      stars.forEach((star) => {
        star.x += star.driftX * delta;
        star.y += star.driftY * delta;
        if (star.x < -.015) star.x = 1.015;
        if (star.x > 1.015) star.x = -.015;
        if (star.y < -.015) star.y = 1.015;
        if (star.y > 1.015) star.y = -.015;

        let x = star.x * width + pointerX * (1.5 + star.depth * 5.5);
        let y = star.y * height + pointerY * (1 + star.depth * 3.5);

        if (shiftEnergy > .001) {
          const dx = x - shiftCenterX;
          const dy = y - shiftCenterY;
          const distance = Math.max(48, Math.hypot(dx, dy));
          const force = shiftEnergy * 7 * (1 - clamp(distance / Math.max(width, height), 0, 1));
          x += dx / distance * force;
          y += dy / distance * force;
        }

        const pulse = 1 - star.twinkleDepth * .5 + Math.sin(time * star.twinkle + star.phase) * star.twinkleDepth * .5;
        drawStar(star, x, y, clamp(star.alpha * pulse, .025, .96));
      });

      if (!reducedMotion && time >= nextMeteorAt) {
        spawnMeteor();
        nextMeteorAt = time + 24000 + Math.random() * 26000;
      }

      meteors = meteors.filter((meteor) => {
        meteor.life += 1;
        meteor.x += meteor.vx;
        meteor.y += meteor.vy;
        const alpha = Math.sin(Math.PI * meteor.life / meteor.maxLife) * .58;
        const magnitude = Math.max(.01, Math.hypot(meteor.vx, meteor.vy));
        const tailX = meteor.x - meteor.vx / magnitude * meteor.length;
        const tailY = meteor.y - meteor.vy / magnitude * meteor.length;
        const gradient = context.createLinearGradient(tailX, tailY, meteor.x, meteor.y);
        gradient.addColorStop(0, 'rgba(255,255,255,0)');
        gradient.addColorStop(.82, `rgba(143,213,255,${alpha * .2})`);
        gradient.addColorStop(1, `rgba(255,255,255,${alpha})`);
        context.strokeStyle = gradient;
        context.lineWidth = .75;
        context.beginPath();
        context.moveTo(tailX, tailY);
        context.lineTo(meteor.x, meteor.y);
        context.stroke();
        return meteor.life < meteor.maxLife;
      });
    };

    const pointerMove = (event) => {
      const rect = hero.getBoundingClientRect();
      targetPointerX = clamp((event.clientX - rect.left) / rect.width - .5, -.5, .5);
      targetPointerY = clamp((event.clientY - rect.top) / rect.height - .5, -.5, .5);
    };

    hero.addEventListener('pointermove', pointerMove, { passive: true });
    hero.addEventListener('pointerleave', () => { targetPointerX = 0; targetPointerY = 0; }, { passive: true });
    addEventListener('resize', resize, { passive: true });

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        visible = entries[0]?.isIntersecting ?? true;
      }, { rootMargin: '100px' });
      observer.observe(hero);
    }

    resize();
    requestAnimationFrame(draw);

    return {
      setPointer(x, y) { targetPointerX = x; targetPointerY = y; },
      setShiftEnergy(value) { shiftEnergy = clamp(value, 0, 1); }
    };
  };

  /* Photoreal WebGL Moon — mapped from NASA LRO colour and elevation data. */
  const createMoonRenderer = (canvas, stage) => {
    if (!canvas || !stage || canvas.dataset.initialized) return null;
    canvas.dataset.initialized = 'true';

    const gl = canvas.getContext('webgl', {
      alpha: true,
      antialias: true,
      premultipliedAlpha: true,
      preserveDrawingBuffer: false,
      powerPreference: 'high-performance'
    });
    if (!gl) return null;

    const vertexSource = `
      attribute vec2 aPosition;
      varying vec2 vUv;
      void main() {
        vUv = aPosition * 0.5 + 0.5;
        gl_Position = vec4(aPosition, 0.0, 1.0);
      }
    `;

    const fragmentSource = `
      precision highp float;
      varying vec2 vUv;
      uniform sampler2D uColorMap;
      uniform sampler2D uHeightMap;
      uniform vec2 uRotation;
      uniform vec2 uLight;
      const float PI = 3.141592653589793;

      vec3 rotateX(vec3 p, float a) {
        float c = cos(a); float s = sin(a);
        return vec3(p.x, c * p.y - s * p.z, s * p.y + c * p.z);
      }
      vec3 rotateY(vec3 p, float a) {
        float c = cos(a); float s = sin(a);
        return vec3(c * p.x + s * p.z, p.y, -s * p.x + c * p.z);
      }

      void main() {
        vec2 p = vUv * 2.0 - 1.0;
        p.y *= -1.0;
        float r2 = dot(p, p);
        if (r2 > 1.0) discard;

        float z = sqrt(max(0.0, 1.0 - r2));
        vec3 viewNormal = normalize(vec3(p.x, p.y, z));
        vec3 surface = rotateY(rotateX(viewNormal, uRotation.y), uRotation.x);

        float longitude = atan(surface.x, surface.z);
        float latitude = asin(clamp(surface.y, -1.0, 1.0));
        vec2 mapUv = vec2(fract(longitude / (2.0 * PI) + 0.5), 0.5 - latitude / PI);

        vec2 texel = vec2(1.0 / 4096.0, 1.0 / 2048.0);
        float hL = texture2D(uHeightMap, mapUv - vec2(texel.x, 0.0)).r;
        float hR = texture2D(uHeightMap, mapUv + vec2(texel.x, 0.0)).r;
        float hD = texture2D(uHeightMap, mapUv - vec2(0.0, texel.y)).r;
        float hU = texture2D(uHeightMap, mapUv + vec2(0.0, texel.y)).r;
        float dx = (hR - hL) * 42.0;
        float dy = (hU - hD) * 42.0;

        vec3 tangent = normalize(vec3(surface.z, 0.0, -surface.x) + vec3(0.00001));
        vec3 bitangent = normalize(cross(surface, tangent));
        vec3 detailNormal = normalize(surface - tangent * dx + bitangent * dy);

        vec3 lightDir = normalize(vec3(0.49 + uLight.x * 0.18, 0.34 + uLight.y * 0.14, 0.82));
        float ndl = dot(detailNormal, lightDir);
        float diffuse = max(ndl, 0.0);
        float terminator = smoothstep(-0.06, 0.14, ndl);
        float softLight = 0.042 + diffuse * 1.04;
        softLight *= mix(0.38, 1.0, terminator);

        vec3 base = pow(texture2D(uColorMap, mapUv).rgb, vec3(2.2));
        float limb = 0.78 + 0.22 * pow(max(viewNormal.z, 0.0), 0.28);
        vec3 colour = base * softLight * limb;

        float rim = pow(1.0 - max(viewNormal.z, 0.0), 5.2) * .09 * terminator;
        colour += vec3(.35, .58, .78) * rim;
        colour = pow(max(colour, 0.0), vec3(1.0 / 2.2));
        colour = min(colour * 1.04, vec3(1.0));

        float alpha = 1.0 - smoothstep(.982, 1.0, r2);
        gl_FragColor = vec4(colour, alpha);
      }
    `;

    const compile = (type, source) => {
      const shader = gl.createShader(type);
      gl.shaderSource(shader, source);
      gl.compileShader(shader);
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        console.warn('Lunar shader failed:', gl.getShaderInfoLog(shader));
        gl.deleteShader(shader);
        return null;
      }
      return shader;
    };

    const vertexShader = compile(gl.VERTEX_SHADER, vertexSource);
    const fragmentShader = compile(gl.FRAGMENT_SHADER, fragmentSource);
    if (!vertexShader || !fragmentShader) return null;

    const program = gl.createProgram();
    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      console.warn('Lunar shader link failed:', gl.getProgramInfoLog(program));
      return null;
    }
    gl.useProgram(program);

    const vertices = new Float32Array([-1,-1, 1,-1, -1,1, -1,1, 1,-1, 1,1]);
    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);
    const position = gl.getAttribLocation(program, 'aPosition');
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);

    const rotationUniform = gl.getUniformLocation(program, 'uRotation');
    const lightUniform = gl.getUniformLocation(program, 'uLight');
    const colorUniform = gl.getUniformLocation(program, 'uColorMap');
    const heightUniform = gl.getUniformLocation(program, 'uHeightMap');

    const createTexture = (source, unit) => new Promise((resolve, reject) => {
      const image = new Image();
      image.decoding = 'async';
      image.onload = () => {
        const texture = gl.createTexture();
        gl.activeTexture(gl.TEXTURE0 + unit);
        gl.bindTexture(gl.TEXTURE_2D, texture);
        gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
        gl.generateMipmap(gl.TEXTURE_2D);
        resolve(texture);
      };
      image.onerror = reject;
      image.src = source;
    });

    let ready = false;
    let visible = true;
    let lastFrame = 0;
    let autoYaw = -.12;
    let dragYaw = 0;
    let dragPitch = -.035;
    let targetYaw = 0;
    let targetPitch = -.035;
    let lightX = 0;
    let lightY = 0;
    let targetLightX = 0;
    let targetLightY = 0;

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = Math.min(devicePixelRatio || 1, innerWidth < 720 ? 1.22 : 1.5);
      const targetWidth = Math.max(2, Math.round(rect.width * dpr));
      const targetHeight = Math.max(2, Math.round(rect.height * dpr));
      if (canvas.width !== targetWidth || canvas.height !== targetHeight) {
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        gl.viewport(0, 0, targetWidth, targetHeight);
      }
    };

    const render = (time) => {
      requestAnimationFrame(render);
      if (!ready || !visible) return;
      if (time - lastFrame < (reducedMotion ? 140 : 33)) return;
      const delta = Math.min(70, Math.max(1, time - lastFrame || 16));
      lastFrame = time;

      if (!reducedMotion) autoYaw += delta * 0.0000032;
      dragYaw = lerp(dragYaw, targetYaw, .065);
      dragPitch = lerp(dragPitch, targetPitch, .065);
      lightX = lerp(lightX, targetLightX, .045);
      lightY = lerp(lightY, targetLightY, .045);

      resize();
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.uniform2f(rotationUniform, autoYaw + dragYaw, dragPitch);
      gl.uniform2f(lightUniform, lightX, lightY);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
    };

    Promise.all([
      createTexture(canvas.dataset.colorMap, 0),
      createTexture(canvas.dataset.heightMap, 1)
    ]).then(() => {
      gl.useProgram(program);
      gl.uniform1i(colorUniform, 0);
      gl.uniform1i(heightUniform, 1);
      ready = true;
      stage.classList.add('webgl-ready');
      resize();
    }).catch((error) => {
      console.warn('Photoreal lunar maps could not load; using the embedded photographic fallback.', error);
    });

    addEventListener('resize', resize, { passive: true });
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        visible = entries[0]?.isIntersecting ?? true;
      }, { rootMargin: '120px' });
      observer.observe(stage);
    }
    requestAnimationFrame(render);

    return {
      rotateBy(dx, dy) {
        targetYaw += dx * .0042;
        targetPitch = clamp(targetPitch + dy * .0032, -.32, .32);
      },
      setLight(x, y) {
        targetLightX = clamp(x, -.5, .5);
        targetLightY = clamp(-y, -.5, .5);
      },
      settle() {
        targetPitch = clamp(targetPitch, -.24, .24);
      }
    };
  };

  /* Ambient signal */
  let ambientController = null;
  const createAmbientController = () => {
    if (ambientController) return ambientController;
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return null;

    const context = new AudioContextClass();
    const master = context.createGain();
    master.gain.value = 0;
    master.connect(context.destination);

    const low = context.createOscillator();
    const lowGain = context.createGain();
    low.type = 'sine';
    low.frequency.value = 54;
    lowGain.gain.value = .018;
    low.connect(lowGain).connect(master);

    const overtone = context.createOscillator();
    const overtoneGain = context.createGain();
    overtone.type = 'sine';
    overtone.frequency.value = 108.4;
    overtoneGain.gain.value = .006;
    overtone.connect(overtoneGain).connect(master);

    const lfo = context.createOscillator();
    const lfoGain = context.createGain();
    lfo.frequency.value = .07;
    lfoGain.gain.value = .004;
    lfo.connect(lfoGain).connect(lowGain.gain);

    low.start();
    overtone.start();
    lfo.start();

    let active = false;
    ambientController = {
      async toggle() {
        if (context.state === 'suspended') await context.resume();
        active = !active;
        master.gain.cancelScheduledValues(context.currentTime);
        master.gain.linearRampToValueAtTime(active ? .5 : 0, context.currentTime + 1.2);
        return active;
      },
      get active() { return active; }
    };
    return ambientController;
  };

  /* Hero interactions and the deliberately slow 37 cm event. */
  const initHero = () => {
    const hero = q('[data-lunar-hero]');
    if (!hero || hero.dataset.initialized) return;
    hero.dataset.initialized = 'true';

    const stage = q('[data-moon-stage]', hero);
    let starfield = null;
    let moonRenderer = null;
    let experienceStarted = false;
    const displacement = q('[data-moon-displacement]', hero);
    const readout = q('[data-movement-value]', hero);
    const skyStatus = q('[data-sky-status]', hero);
    const liveStatus = q('[data-live-status]', hero);
    const shiftProgress = q('[data-shift-progress]', hero);
    const moveButtons = qa('[data-move-moon]', hero);
    const ambientButtons = qa('[data-ambient-toggle]', document);
    const shiftPixels = Number(hero.dataset.shiftPixels) || 14;
    const shiftDuration = Number(hero.dataset.shiftDuration) || 11000;
    const shiftDelay = Number(hero.dataset.shiftDelay) || 28000;
    const autoShift = hero.dataset.autoShift !== 'false';

    let displacementRunning = false;
    let displacementComplete = false;
    let shiftRaf = 0;
    let autoTimer = 0;
    const shiftDirection = -1;

    const setShift = (pixels, centimetres) => {
      hero.style.setProperty('--event-shift', `${pixels.toFixed(3)}px`);
      hero.style.setProperty('--shift-progress', String(clamp(centimetres / 37, 0, 1)));
      if (readout) readout.textContent = `+${centimetres.toFixed(2).padStart(5, '0')} cm`;
      if (shiftProgress) shiftProgress.style.width = `${clamp(centimetres / 37, 0, 1) * 100}%`;
    };

    const updateButtonLabel = (label) => moveButtons.forEach((button) => { button.textContent = label; });

    const startDisplacement = (manual = false) => {
      clearTimeout(autoTimer);
      if (displacementRunning) return;
      if (displacementComplete) {
        displacementComplete = false;
        hero.classList.remove('has-displaced');
        setShift(0, 0);
        updateButtonLabel('BEGIN 37 CM SHIFT');
        if (skyStatus) skyStatus.textContent = 'RECALIBRATING';
        setTimeout(() => startDisplacement(manual), 900);
        return;
      }

      displacementRunning = true;
      hero.classList.add('is-displacing');
      if (skyStatus) skyStatus.textContent = 'SHIFTING LEFT';
      if (liveStatus) liveStatus.textContent = 'TRACKING MOVEMENT';
      updateButtonLabel('MOON SHIFTING...');
      const start = performance.now();

      const step = (now) => {
        const raw = clamp((now - start) / shiftDuration, 0, 1);
        const eased = smoothstep(raw);
        setShift(shiftPixels * eased * shiftDirection, 37 * eased);
        starfield?.setShiftEnergy(Math.pow(Math.sin(Math.PI * raw), 1.7));

        if (raw > .32 && raw < .72 && skyStatus) skyStatus.textContent = 'REALITY UNSTABLE';
        if (raw < 1) {
          shiftRaf = requestAnimationFrame(step);
          return;
        }

        displacementRunning = false;
        displacementComplete = true;
        hero.classList.remove('is-displacing');
        hero.classList.add('has-displaced');
        starfield?.setShiftEnergy(0);
        if (skyStatus) skyStatus.textContent = 'UPDATED';
        if (liveStatus) liveStatus.textContent = 'POSITION REWRITTEN';
        updateButtonLabel('REPLAY THE 37 CM SHIFT');
        if (manual && navigator.vibrate) navigator.vibrate(16);
      };
      shiftRaf = requestAnimationFrame(step);
    };

    moveButtons.forEach((button) => button.addEventListener('click', () => startDisplacement(true)));

    const pointerMove = (event) => {
      const rect = hero.getBoundingClientRect();
      const nx = clamp((event.clientX - rect.left) / rect.width - .5, -.5, .5);
      const ny = clamp((event.clientY - rect.top) / rect.height - .5, -.5, .5);
      hero.style.setProperty('--pointer-x', `${nx * 9}px`);
      hero.style.setProperty('--pointer-y', `${ny * 7}px`);
      starfield?.setPointer(nx, ny);
      moonRenderer?.setLight(nx, ny);
      stage?.style.setProperty('--light-x', `${nx * 7}%`);
      stage?.style.setProperty('--light-y', `${ny * 7}%`);
    };
    hero.addEventListener('pointermove', pointerMove, { passive: true });

    stage?.addEventListener('click', () => startDisplacement(true));
    stage?.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        startDisplacement(true);
      }
    });

    ambientButtons.forEach((button) => {
      if (button.dataset.ambientBound) return;
      button.dataset.ambientBound = 'true';
      button.addEventListener('click', async () => {
        const controller = createAmbientController();
        if (!controller) return;
        const active = await controller.toggle();
        qa('[data-ambient-toggle]').forEach((toggle) => {
          toggle.classList.toggle('active', active);
          const label = q('[data-ambient-label]', toggle);
          if (label) label.textContent = `AMBIENT SIGNAL: ${active ? 'ON' : 'OFF'}`;
          else toggle.textContent = `AMBIENT SIGNAL: ${active ? 'ON' : 'OFF'}`;
        });
      });
    });

    const scheduleAutoShift = () => {
      if (!autoShift || reducedMotion) return;
      autoTimer = window.setTimeout(() => {
        if (!displacementRunning && !displacementComplete && document.visibilityState === 'visible') startDisplacement(false);
      }, shiftDelay);
    };

    const activateExperience = () => {
      if (experienceStarted) return;
      experienceStarted = true;
      hero.classList.add('experience-live');
      starfield = createStarfield(q('[data-starfield]', hero), hero);
      moonRenderer = createMoonRenderer(q('[data-moon-webgl]', hero), stage);
      scheduleAutoShift();
    };

    initIntro(hero, activateExperience);

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) cancelAnimationFrame(shiftRaf);
    });
  };

  /* Command console */
  const initCommandConsole = (root = document) => {
    const command = q('#command', root) || (root.matches?.('#command') ? root : null);
    if (!command || command.dataset.initialized) return;
    command.dataset.initialized = 'true';

    qa('[data-command-card]', command).forEach((card) => {
      card.addEventListener('pointermove', (event) => {
        const rect = card.getBoundingClientRect();
        card.style.setProperty('--card-x', `${((event.clientX - rect.left) / rect.width) * 100}%`);
        card.style.setProperty('--card-y', `${((event.clientY - rect.top) / rect.height) * 100}%`);
      }, { passive: true });
    });

    const timelineState = q('[data-timeline-state]', command);
    qa('[data-mini-event]', command).forEach((button) => button.addEventListener('click', () => {
      qa('[data-mini-event]', command).forEach((item) => item.classList.remove('active'));
      button.classList.add('active');
      if (timelineState) timelineState.textContent = `${button.dataset.time} / ${button.dataset.state}`;
    }));

    const witnessName = q('[data-witness-name]', command);
    const witnessStatus = q('[data-witness-status]:not(button)', command);
    qa('button[data-witness]', command).forEach((button) => button.addEventListener('click', () => {
      qa('button[data-witness]', command).forEach((item) => item.classList.remove('active'));
      button.classList.add('active');
      if (witnessName) witnessName.textContent = button.dataset.witness;
      if (witnessStatus) witnessStatus.textContent = button.dataset.witnessStatus;
    }));

    const signalButton = q('[data-command-signal]', command);
    signalButton?.addEventListener('click', () => {
      const active = signalButton.classList.toggle('active');
      const label = q('[data-command-signal-label]', signalButton);
      if (label) label.textContent = active ? 'SIGNAL ACTIVE' : 'ACTIVATE SIGNAL';
    });

    const radar = q('[data-signal-radar]', command);
    radar?.addEventListener('click', () => {
      radar.classList.remove('pulsing');
      void radar.offsetWidth;
      radar.classList.add('pulsing');
      const strength = 72 + Math.floor(Math.random() * 25);
      const strengthText = q('[data-signal-strength]', command);
      const quality = q('[data-signal-quality]', command);
      const bar = q('[data-signal-bar]', command);
      if (strengthText) strengthText.textContent = `${strength}%`;
      if (bar) bar.style.width = `${strength}%`;
      if (quality) quality.textContent = strength > 89 ? 'LOCKED' : strength > 79 ? 'STABLE' : 'SEARCHING';
    });
  };

  /* Scroll reveals and typed lines */
  const initReveals = (root = document) => {
    const elements = qa('[data-reveal]', root).filter((element) => !element.dataset.revealBound);
    if (!elements.length) return;

    if (!('IntersectionObserver' in window) || reducedMotion) {
      elements.forEach((element) => {
        element.dataset.revealBound = 'true';
        element.classList.add('revealed');
      });
      return;
    }

    const observer = new IntersectionObserver((entries) => entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('revealed');
      observer.unobserve(entry.target);
    }), { threshold: .1, rootMargin: '0px 0px -5% 0px' });

    elements.forEach((element) => {
      element.dataset.revealBound = 'true';
      element.classList.add('reveal-ready');
      observer.observe(element);
    });

    qa('[data-typed-lines]', root).forEach((typed) => {
      if (typed.dataset.initialized) return;
      typed.dataset.initialized = 'true';
      const typedObserver = new IntersectionObserver((entries) => entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        qa('p', typed).forEach((paragraph, index) => setTimeout(() => paragraph.classList.add('visible'), index * 470));
        typedObserver.disconnect();
      }), { threshold: .35 });
      typedObserver.observe(typed);
    });
  };

  /* Event tabs */
  const initEventTabs = (root = document) => {
    qa('.event-console', root).forEach((consoleElement) => {
      if (consoleElement.dataset.initialized) return;
      consoleElement.dataset.initialized = 'true';
      const buttons = qa('[data-event-tab]', consoleElement);
      const panels = qa('.event-panel', consoleElement);
      buttons.forEach((button) => button.addEventListener('click', () => {
        const index = Number(button.dataset.eventTab);
        buttons.forEach((item) => {
          item.classList.remove('active');
          item.setAttribute('aria-pressed', 'false');
        });
        panels.forEach((panel) => panel.classList.remove('active'));
        button.classList.add('active');
        button.setAttribute('aria-pressed', 'true');
        panels[index]?.classList.add('active');
      }));
    });
  };

  /* Dossiers and subtle card tilt */
  const initDossiers = (root = document) => {
    const dossierData = {
      nova: {
        title: 'Nova Mercer',
        label: 'SUBJECT 01 / MEMORY RETAINED',
        copy: 'Nova remembers the original world in impossible detail. His golden eyes appeared the morning after the movement. The radio in his apartment has spoken his name every night since.'
      },
      kai: {
        title: 'Kai Arlen',
        label: 'SUBJECT 02 / SIGNAL ENGINEER',
        copy: 'Kai has spent years proving that reality contains seams. Her receiver was designed to detect cosmic background noise. Instead, it found a voice beneath the Moon.'
      }
    };
    const dialog = q('[data-dossier-dialog]', root) || q('[data-dossier-dialog]');
    qa('[data-dossier]', root).forEach((button) => {
      if (button.dataset.initialized) return;
      button.dataset.initialized = 'true';
      button.addEventListener('click', () => {
        const dossier = dossierData[button.dataset.dossier];
        if (!dossier || !dialog) return;
        q('[data-dialog-title]', dialog).textContent = dossier.title;
        q('[data-dialog-label]', dialog).textContent = dossier.label;
        q('[data-dialog-copy]', dialog).textContent = dossier.copy;
        dialog.showModal?.();
      });
    });
    const close = dialog ? q('[data-dialog-close]', dialog) : null;
    if (close && !close.dataset.initialized) {
      close.dataset.initialized = 'true';
      close.addEventListener('click', () => dialog.close?.());
    }

    qa('[data-tilt]', root).forEach((element) => {
      if (element.dataset.tiltBound || reducedMotion) return;
      element.dataset.tiltBound = 'true';
      const strength = Number(element.dataset.tiltStrength) || 4;
      element.addEventListener('pointermove', (event) => {
        if (matchMedia('(pointer: coarse)').matches) return;
        const rect = element.getBoundingClientRect();
        const x = (event.clientX - rect.left) / rect.width - .5;
        const y = (event.clientY - rect.top) / rect.height - .5;
        element.style.transform = `perspective(1000px) rotateX(${y * -strength}deg) rotateY(${x * strength}deg) translateY(-2px)`;
      }, { passive: true });
      element.addEventListener('pointerleave', () => { element.style.transform = ''; }, { passive: true });
    });
  };

  /* V6.6 reader: forced-aligned word highlighting, two narrated releases and saved listening positions */
  const initReader = (root = document) => {
    const reader = q('[data-reader]', root) || q('[data-reader]');
    if (!reader || reader.dataset.initialized) return;
    reader.dataset.initialized = 'true';

    const content = q('[data-reader-content]', reader);
    const progress = q('[data-reader-progress]', reader);
    const updateProgress = () => {
      if (!progress || !content) return;
      const rect = content.getBoundingClientRect();
      const height = Math.max(1, content.offsetHeight - innerHeight);
      const ratio = clamp(-rect.top / height, 0, 1);
      progress.style.width = `${ratio * 100}%`;
    };
    updateProgress();
    addEventListener('scroll', updateProgress, { passive: true });
    addEventListener('resize', updateProgress, { passive: true });

    const focusButton = q('[data-reader-mode]', reader);
    const setFocusMode = (active) => {
      document.body.classList.toggle('focus-reader', active);
      if (focusButton) focusButton.textContent = active ? 'EXIT FOCUS MODE' : 'FOCUS MODE';
    };
    focusButton?.addEventListener('click', () => setFocusMode(!document.body.classList.contains('focus-reader')));
    addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && document.body.classList.contains('focus-reader')) setFocusMode(false);
    });

    const navItems = qa('[data-story-nav]', reader);
    if ('IntersectionObserver' in window) {
      const navObserver = new IntersectionObserver((entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (!visible) return;
        navItems.forEach((item) => item.classList.toggle('active', item.dataset.storyNav === visible.target.dataset.releaseCopy));
      }, { rootMargin: '-18% 0px -58% 0px', threshold: [0, .15, .35] });
      qa('[data-release-copy]', reader).forEach((release) => navObserver.observe(release));
    }

    const players = qa('[data-narration-player]', reader);
    const instances = new Map();
    const rates = [.9, 1, 1.1, 1.25];

    const pauseOthers = (currentPlayer) => {
      instances.forEach((instance, player) => {
        if (player !== currentPlayer && !instance.audio.paused) instance.audio.pause();
      });
    };

    players.forEach((player) => {
      const source = (player.dataset.audioSrc || '').trim();
      const releaseId = player.dataset.releaseId || 'release';
      const copy = q(player.dataset.copyTarget, reader);
      if (!source || !copy) return;

      const audio = new Audio(source);
      audio.preload = 'metadata';
      audio.preservesPitch = true;
      const main = q('[data-narrate]', player);
      const label = q('[data-narrate-label]', player);
      const icon = q('[data-narrate-icon]', player);
      const status = q('[data-narration-status]', player);
      const seek = q('[data-narration-seek]', player);
      const current = q('[data-narration-current]', player);
      const duration = q('[data-narration-duration]', player);
      const back = q('[data-narration-back]', player);
      const forward = q('[data-narration-forward]', player);
      const speed = q('[data-narration-speed]', player);
      const nodes = Array.from(copy.children).filter((node) => {
        if (!['P', 'H3', 'DIV'].includes(node.tagName)) return false;
        if (node.classList.contains('release-code')) return false;
        if (node.classList.contains('reader-lock') || node.classList.contains('reader-continue')) return false;
        return node.tagName !== 'DIV' || node.classList.contains('prologue-clock');
      });
      const cueRows = window.TNMM_NARRATION_CUES?.[releaseId] || [];
      const cues = cueRows.map((row) => ({ start: Number(row[0]), end: Number(row[1]), word: String(row[2]) }));
      let rateIndex = 1;
      let raf = 0;
      let lastPaint = 0;
      let lastSavedSecond = -1;
      let activeWord = null;
      let activeLine = null;
      const storageKey = `tnmmAudio:${location.pathname}:${releaseId}:${source}`;
      const playLabel = player.dataset.playLabel || 'LISTEN';
      const readyStatus = player.dataset.readyStatus || 'READY TO LISTEN';
      const playingStatus = player.dataset.playingStatus || 'PLAYING';
      const wordPattern = /[A-Za-z0-9]+(?:['’][A-Za-z]+)?/g;
      const normaliseWord = (value) => String(value || '').toLowerCase().replace(/[’']/g, '');

      const wrapNarrationWords = () => {
        const spans = [];
        nodes.forEach((node) => {
          const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT, {
            acceptNode(textNode) {
              if (!textNode.nodeValue || !wordPattern.test(textNode.nodeValue)) {
                wordPattern.lastIndex = 0;
                return NodeFilter.FILTER_REJECT;
              }
              wordPattern.lastIndex = 0;
              return NodeFilter.FILTER_ACCEPT;
            }
          });
          const textNodes = [];
          while (walker.nextNode()) textNodes.push(walker.currentNode);
          textNodes.forEach((textNode) => {
            const value = textNode.nodeValue;
            const fragment = document.createDocumentFragment();
            let cursor = 0;
            wordPattern.lastIndex = 0;
            let match;
            while ((match = wordPattern.exec(value)) !== null) {
              if (match.index > cursor) fragment.append(document.createTextNode(value.slice(cursor, match.index)));
              const span = document.createElement('span');
              span.className = 'narration-word';
              span.textContent = match[0];
              span.dataset.narrationToken = normaliseWord(match[0]);
              span.dataset.narrationLine = String(nodes.indexOf(node));
              fragment.append(span);
              spans.push(span);
              cursor = match.index + match[0].length;
            }
            if (cursor < value.length) fragment.append(document.createTextNode(value.slice(cursor)));
            textNode.replaceWith(fragment);
          });
        });
        return spans;
      };

      const words = wrapNarrationWords();
      const mismatches = [];
      const comparableLength = Math.min(words.length, cues.length);
      for (let index = 0; index < comparableLength; index += 1) {
        if (normaliseWord(words[index].dataset.narrationToken) !== normaliseWord(cues[index].word)) {
          mismatches.push(index);
          if (mismatches.length > 4) break;
        }
      }
      const exactSync = words.length === cues.length && mismatches.length === 0;
      player.dataset.syncMode = exactSync ? 'word-timestamps' : 'disabled';
      if (!exactSync) {
        console.warn(`Narration sync disabled for ${releaseId}: cue/text mismatch`, {
          displayedWords: words.length,
          timedWords: cues.length,
          mismatches
        });
      } else {
        words.forEach((word, index) => {
          word.dataset.start = String(cues[index].start);
          word.dataset.end = String(cues[index].end);
        });
      }

      const setState = (state) => {
        if (!label || !icon) return;
        player.classList.toggle('is-playing', state === 'playing');
        if (state === 'playing') { label.textContent = 'PAUSE'; icon.textContent = 'Ⅱ'; }
        else if (state === 'paused') { label.textContent = 'RESUME'; icon.textContent = '▶'; }
        else if (state === 'complete') { label.textContent = 'LISTEN AGAIN'; icon.textContent = '↻'; }
        else { label.textContent = playLabel; icon.textContent = '▶'; }
      };

      const clearNarrationHighlight = () => {
        activeWord?.classList.remove('is-narrating-word');
        activeLine?.classList.remove('is-narrating-line');
        activeWord = null;
        activeLine = null;
        copy.classList.remove('narration-reading');
      };

      const findPrecedingCue = (time) => {
        let low = 0;
        let high = cues.length - 1;
        let answer = -1;
        while (low <= high) {
          const middle = (low + high) >> 1;
          if (cues[middle].start <= time) { answer = middle; low = middle + 1; }
          else high = middle - 1;
        }
        return answer;
      };

      const syncHighlight = () => {
        if (!exactSync || !cues.length || !words.length || audio.paused || audio.ended) {
          if (audio.paused || audio.ended) clearNarrationHighlight();
          return;
        }
        const time = audio.currentTime;
        const preceding = findPrecedingCue(time);
        let wordIndex = -1;
        if (preceding >= 0 && time <= cues[preceding].end + 0.07) wordIndex = preceding;
        const nextIndex = preceding + 1;
        if (wordIndex < 0 && nextIndex < cues.length && time >= cues[nextIndex].start - 0.025) wordIndex = nextIndex;
        const lineIndex = wordIndex >= 0 ? Number(words[wordIndex].dataset.narrationLine) : (preceding >= 0 ? Number(words[preceding].dataset.narrationLine) : -1);
        const nextWord = wordIndex >= 0 ? words[wordIndex] : null;
        const nextLine = lineIndex >= 0 ? nodes[lineIndex] : null;

        if (activeWord !== nextWord) {
          activeWord?.classList.remove('is-narrating-word');
          activeWord = nextWord;
          activeWord?.classList.add('is-narrating-word');
        }
        if (activeLine !== nextLine) {
          activeLine?.classList.remove('is-narrating-line');
          activeLine = nextLine;
          activeLine?.classList.add('is-narrating-line');
        }
        copy.classList.add('narration-reading');
      };

      const update = () => {
        const ratio = audio.duration ? audio.currentTime / audio.duration : 0;
        if (seek) seek.value = String(ratio * 100);
        if (current) current.textContent = formatTime(audio.currentTime);
        if (duration) duration.textContent = audio.duration ? formatTime(audio.duration) : '--:--';
        syncHighlight();
        const wholeSecond = Math.floor(audio.currentTime);
        if (wholeSecond > 2 && wholeSecond % 5 === 0 && wholeSecond !== lastSavedSecond) {
          lastSavedSecond = wholeSecond;
          try { localStorage.setItem(storageKey, String(audio.currentTime)); } catch (_) { /* optional */ }
        }
      };

      const animationLoop = (stamp) => {
        if (stamp - lastPaint >= 30) {
          lastPaint = stamp;
          update();
        }
        if (!audio.paused && !audio.ended) raf = requestAnimationFrame(animationLoop);
      };

      const startAnimation = () => {
        cancelAnimationFrame(raf);
        lastPaint = 0;
        raf = requestAnimationFrame(animationLoop);
      };

      audio.addEventListener('loadedmetadata', () => {
        try {
          const saved = Number(localStorage.getItem(storageKey));
          if (Number.isFinite(saved) && saved > 3 && saved < audio.duration - 6) audio.currentTime = saved;
        } catch (_) { /* optional */ }
        update();
        if (status && exactSync) status.textContent = `${readyStatus} · WORD HIGHLIGHTING READY`;
      });
      audio.addEventListener('timeupdate', update);
      audio.addEventListener('seeking', update);
      audio.addEventListener('seeked', update);
      audio.addEventListener('ratechange', update);
      audio.addEventListener('play', () => {
        pauseOthers(player);
        setState('playing');
        if (status) status.textContent = exactSync ? `${playingStatus} · WORD HIGHLIGHTING ACTIVE` : playingStatus;
        startAnimation();
      });
      audio.addEventListener('pause', () => {
        cancelAnimationFrame(raf);
        if (audio.ended) return;
        setState(audio.currentTime > 0 ? 'paused' : 'idle');
        if (status) status.textContent = audio.currentTime > 0 ? 'PAUSED' : readyStatus;
        clearNarrationHighlight();
      });
      audio.addEventListener('ended', () => {
        cancelAnimationFrame(raf);
        setState('complete');
        if (status) status.textContent = releaseId === 'prologue' ? 'PROLOGUE COMPLETE · CONTINUE TO CHAPTER ONE' : 'CHAPTER ONE COMPLETE';
        clearNarrationHighlight();
        try { localStorage.removeItem(storageKey); } catch (_) { /* optional */ }
      });

      main?.addEventListener('click', () => {
        if (audio.ended) audio.currentTime = 0;
        if (audio.paused) audio.play().catch(() => { if (status) status.textContent = 'TAP AGAIN TO BEGIN AUDIO'; });
        else audio.pause();
      });
      seek?.addEventListener('input', () => {
        if (!audio.duration) return;
        audio.currentTime = Number(seek.value) / 100 * audio.duration;
        update();
      });
      back?.addEventListener('click', () => {
        audio.currentTime = Math.max(0, audio.currentTime - 15);
        update();
      });
      forward?.addEventListener('click', () => {
        audio.currentTime = Math.min(audio.duration || Infinity, audio.currentTime + 15);
        update();
      });
      speed?.addEventListener('click', () => {
        rateIndex = (rateIndex + 1) % rates.length;
        audio.playbackRate = rates[rateIndex];
        speed.textContent = `${rates[rateIndex]}×`;
        update();
      });

      instances.set(player, { audio, copy, status, words, cues, exactSync, update });
    });

    qa('[data-narration-trigger]').forEach((trigger) => {
      if (trigger.dataset.narrationBound) return;
      trigger.dataset.narrationBound = 'true';
      trigger.addEventListener('click', () => {
        const targetId = trigger.dataset.narrationTarget || 'prologue';
        const targetPlayer = players.find((player) => player.dataset.releaseId === targetId) || players[0];
        const instance = instances.get(targetPlayer);
        document.querySelector(`#${targetId}`)?.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
        window.setTimeout(() => {
          if (!instance) return;
          if (instance.audio.ended) instance.audio.currentTime = 0;
          instance.audio.play().catch(() => { if (instance.status) instance.status.textContent = 'PRESS PLAY TO BEGIN'; });
        }, reducedMotion ? 0 : 650);
      });
    });
  };



/* V6.14 anomaly lab */
const initRealityAtlas = (root = document) => {
  qa('[data-atlas-root]', root).forEach((section) => {
    if (section.dataset.initialized) return;
    section.dataset.initialized = 'true';

    qa('[data-depth-card]', section).forEach((card) => {
      card.addEventListener('pointermove', (event) => {
        const rect = card.getBoundingClientRect();
        card.style.setProperty('--card-x', `${((event.clientX - rect.left) / rect.width) * 100}%`);
        card.style.setProperty('--card-y', `${((event.clientY - rect.top) / rect.height) * 100}%`);
      }, { passive: true });
    });

    const statusOut = q('[data-atlas-status]', section);
    const labelOut = q('[data-atlas-label-out]', section);
    const titleOut = q('[data-atlas-title-out]', section);
    const copyOut = q('[data-atlas-copy-out]', section);
    const atlasPoints = qa('[data-atlas-point]', section);
    const activatePoint = (button) => {
      atlasPoints.forEach((item) => item.classList.toggle('is-active', item === button));
      if (statusOut) statusOut.textContent = button.dataset.atlasStatus || '';
      if (labelOut) labelOut.textContent = button.dataset.atlasLabel || '';
      if (titleOut) titleOut.textContent = button.dataset.atlasTitle || '';
      if (copyOut) copyOut.textContent = button.dataset.atlasCopy || '';
    };
    atlasPoints.forEach((button) => button.addEventListener('click', () => activatePoint(button)));
    if (atlasPoints[0]) activatePoint(atlasPoints.find((item) => item.classList.contains('is-active')) || atlasPoints[0]);

    const shiftValue = q('[data-shift-value]', section);
    const shiftState = q('[data-shift-state]', section);
    const shiftCopy = q('[data-shift-copy]', section);
    const shiftFill = q('[data-shift-fill]', section);
    const shiftRange = q('[data-shift-range]', section);
    const shiftStates = [
      { max: 0, label: '00.00 CM / THE NIGHT', copy: 'Before the movement, everything is still ordinary. The instruments trust the sky. Humanity still belongs entirely to the version of reality it thinks it knows.' },
      { max: 11, label: '11.00 CM / THE SHIFT', copy: 'At eleven seconds, the instruments still insist they are telling the truth. The Moon has moved. The world has not caught up yet.' },
      { max: 22, label: '22.00 CM / THE SILENCE', copy: 'The silence arrives after the fact. Records begin correcting themselves. The official version of events hardens before most people even realise anything changed.' },
      { max: 37, label: '37.00 CM / THE DISCREPANCY', copy: 'Thirty-seven centimetres is all it takes. Small enough to sound harmless. Large enough to fracture certainty and leave a few people remembering the wrong world.' }
    ];
    const updateShift = (value) => {
      const numeric = clamp(Number(value) || 0, 0, 37);
      if (shiftValue) shiftValue.textContent = `${numeric.toFixed(2)}`;
      if (shiftFill) shiftFill.style.width = `${(numeric / 37) * 100}%`;
      const state = shiftStates.find((item) => numeric <= item.max) || shiftStates[shiftStates.length - 1];
      if (shiftState) shiftState.textContent = numeric === 0 ? state.label : `${numeric.toFixed(2)} CM / ${state.label.split(' / ')[1]}`;
      if (shiftCopy) shiftCopy.textContent = state.copy;
    };
    if (shiftRange) {
      shiftRange.addEventListener('input', () => updateShift(shiftRange.value));
      qa('[data-shift-jump]', section).forEach((button) => button.addEventListener('click', () => {
        shiftRange.value = button.dataset.shiftJump;
        updateShift(shiftRange.value);
      }));
      updateShift(shiftRange.value);
    }

    const memoryMeta = q('[data-memory-meta-out]', section);
    const memoryTitle = q('[data-memory-title-out]', section);
    const memoryCopy = q('[data-memory-copy-out]', section);
    const memoryTabs = qa('[data-memory-tab]', section);
    const activateMemory = (button) => {
      memoryTabs.forEach((item) => {
        const active = item === button;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-selected', String(active));
      });
      if (memoryMeta) memoryMeta.textContent = button.dataset.memoryMeta || '';
      if (memoryTitle) memoryTitle.textContent = button.dataset.memoryTitle || '';
      if (memoryCopy) memoryCopy.textContent = button.dataset.memoryCopy || '';
    };
    memoryTabs.forEach((button) => button.addEventListener('click', () => activateMemory(button)));
    if (memoryTabs[0]) activateMemory(memoryTabs.find((item) => item.classList.contains('is-active')) || memoryTabs[0]);

    const constellationState = q('[data-constellation-state]', section);
    const constellationCode = q('[data-constellation-code]', section);
    const constellationTitle = q('[data-constellation-title]', section);
    const constellationCopy = q('[data-constellation-copy]', section);
    const constellationNodes = qa('[data-constellation-node]', section);
    const activateNode = (button) => {
      constellationNodes.forEach((item) => item.classList.toggle('is-active', item === button));
      if (constellationState) constellationState.textContent = `DECODED / ${button.dataset.nodeCode || ''}`;
      if (constellationCode) constellationCode.textContent = button.dataset.nodeCode || '';
      if (constellationTitle) constellationTitle.textContent = button.dataset.nodeTitle || '';
      if (constellationCopy) constellationCopy.textContent = button.dataset.nodeCopy || '';
    };
    constellationNodes.forEach((button) => button.addEventListener('click', () => activateNode(button)));
    if (constellationNodes[0]) activateNode(constellationNodes.find((item) => item.classList.contains('is-active')) || constellationNodes[0]);
  });
};

  /* Transmission cards and signup radar */
  const initSignals = (root = document) => {
    qa('[data-play-signal]', root).forEach((button) => {
      if (button.dataset.initialized) return;
      button.dataset.initialized = 'true';
      button.addEventListener('click', () => {
        const card = button.closest('.transmission-card');
        const active = card?.classList.toggle('playing') || false;
        button.textContent = active ? 'STOP SIGNAL' : 'PLAY SIGNAL';
      });
    });

    qa('[data-pulse-orb]', root).forEach((orb) => {
      if (orb.dataset.initialized) return;
      orb.dataset.initialized = 'true';
      orb.addEventListener('click', () => {
        orb.classList.remove('pulsing');
        void orb.offsetWidth;
        orb.classList.add('pulsing');
      });
    });


    qa('[data-archive-beacon]', root).forEach((beacon) => {
      if (beacon.dataset.archiveInitialized) return;
      beacon.dataset.archiveInitialized = 'true';
      beacon.addEventListener('click', () => {
        const wrap = beacon.closest('[data-archive-beacon-wrap]');
        const fragment = wrap?.querySelector('[data-archive-fragment]');
        const open = beacon.getAttribute('aria-expanded') !== 'true';
        beacon.setAttribute('aria-expanded', String(open));
        wrap?.classList.toggle('is-open', open);
        fragment?.setAttribute('aria-hidden', String(!open));
      });
    });
  };

  const initAll = (root = document) => {
    initHeader();
    initHero();
    initCommandConsole(root);
    initRealityAtlas(root);
    initReveals(root);
    initEventTabs(root);
    initDossiers(root);
    initReader(root);
    initSignals(root);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
